import { FieldType } from "../../type-system/FieldType";
import {
  ShopifyProductVariantKey,
  ShopifySellingPlanKey,
  ShopifyShopKey,
  ShopifySubscriptionContractKey,
  ShopifySubscriptionLineKey,
  ShopifySubscriptionLineToProductVariantKey,
  ShopifySubscriptionLineToSellingPlanKey,
  ShopifySubscriptionLineToShopKey,
  ShopifySubscriptionLineToSubscriptionContractKey,
  ShopifySubscriptionLineToSubscriptionOriginContractKey,
} from "./keys";
import { ShopifyApiVersion } from "./ShopifyApiVersion";
import { ShopifyDominion } from "./ShopifyDominion";
import type { ConnectionModelSpecVersion } from "./ShopifyMetadata";
import { buildConnectionSpec } from "./ShopifyMetadata";

const specs: ConnectionModelSpecVersion = {
  key: ShopifySubscriptionLineKey,
  "2022-01": {
    name: "Shopify Subscription Line",
    fields: [
      {
        name: "Current Price",
        fieldType: FieldType.JSON,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "currentPrice",
            graphQLSelection: `amount
        currencyCode`,
          },
        },
      },
      {
        name: "Custom Attributes",
        fieldType: FieldType.JSON,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "customAttributes",
            graphQLSelection: `
        key
        value`,
          },
        },
      },
      {
        name: "Discount Allocations",
        fieldType: FieldType.JSON,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "discountAllocations",
            graphQLSelection: `amount {
          amount
          currencyCode
        }
        discount`,
          },
        },
      },
      {
        name: "Admin GraphQL API ID",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "id" } },
      },
      {
        name: "Line Discounted Price",
        fieldType: FieldType.JSON,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "lineDiscountedPrice",
            graphQLSelection: `amount
        currencyCode`,
          },
        },
      },
      {
        name: "Subscription Pricing Policy",
        fieldType: FieldType.JSON,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "pricingPolicy",
            graphQLSelection: `basePrice {
          amount
          currencyCode
        }
        cycleDiscounts {
          adjustmentType
          adjustmentValue {
            ...on MoneyV2 {
              amount
              currencyCode
            }
            ...on SellingPlanPricingPolicyPercentageValue  {
              percentage
            }
          }
          afterCycle
          computedPrice {
            amount
            currencyCode
          }
        }`,
          },
        },
      },
      {
        name: "Quantity",
        fieldType: FieldType.Number,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "quantity" } },
      },
      {
        name: "Requires Shipping",
        fieldType: FieldType.Boolean,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "requiresShipping" } },
      },
      {
        name: "Selling Plan",
        fieldType: FieldType.BelongsTo,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "sellingPlanId" } },
        key: ShopifySubscriptionLineToSellingPlanKey,
        configuration: {
          relatedModelKey: ShopifySellingPlanKey,
        },
      },
      {
        name: "SKU",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "sku" } },
      },
      {
        name: "Taxable",
        fieldType: FieldType.Boolean,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "taxable" } },
      },
      {
        name: "Subscription Contract",
        fieldType: FieldType.BelongsTo,
        key: ShopifySubscriptionLineToSubscriptionContractKey,
        contributorConfiguration: {
          [ShopifyDominion.id]: { graphQLFieldKey: "subscriptionContract", skipGraphQLSelection: true },
        },
        configuration: {
          relatedModelKey: ShopifySubscriptionContractKey,
        },
      },
      {
        name: "Title",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "title" } },
      },
      {
        name: "Variant",
        fieldType: FieldType.BelongsTo,
        key: ShopifySubscriptionLineToProductVariantKey,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "variantId" } },
        configuration: {
          relatedModelKey: ShopifyProductVariantKey,
        },
      },
      {
        name: "Shop",
        fieldType: FieldType.BelongsTo,
        key: ShopifySubscriptionLineToShopKey,
        configuration: {
          relatedModelKey: ShopifyShopKey,
        },
      },
    ],
    syncConfig: {
      type: "graphql_sublist",
      resource: "subscriptionContract.lines",
      parent: "subscriptionContracts",
    },
  },
  "2025-01": {
    fields: [
      {
        name: "Concatenated Origin Contract",
        fieldType: FieldType.BelongsTo,
        key: ShopifySubscriptionLineToSubscriptionOriginContractKey,
        contributorConfiguration: {
          [ShopifyDominion.id]: { graphQLFieldKey: "concatenatedOriginContract", graphQLSelection: "id" },
        },
        configuration: {
          relatedModelKey: ShopifySubscriptionContractKey,
        },
      },
    ],
  },
};

export const ShopifySubscriptionLine = ({ apiVersion }: { apiVersion: ShopifyApiVersion }) => buildConnectionSpec(specs, apiVersion);
